import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { MenuItem, Divider } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { tokens } from '../../theme.js'
import { Subheading, MutedText, IconOnlyButton, ActionMenu } from '../styles/index.js'
import { subtreeIds } from './directory.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body
export const FILE_IDS_TYPE = 'application/x-file-ids'
export const FOLDER_IDS_TYPE = 'application/x-folder-ids'

const Column = styled.aside`
  display: flex;
  flex-direction: column;
  width: 280px;
  min-width: 280px;
  background: ${colors.background.light};
  border-right: 1px solid ${colors.sectionDivider};
  overflow: hidden;
`
const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 8px 10px 20px;
`
const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
`
const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px 0 ${p => 8 + p.$depth * 18}px;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;
  font: ${p => (p.$active ? 600 : 400)} 14px/1.3 ${fontFamily};
  color: ${colors.text.body};
  background: ${p => (p.$drop ? colors.primaryLight : p.$active || p.$selected ? colors.primaryExtraLight : 'transparent')};
  outline: ${p => (p.$drop ? `1.5px solid ${colors.primary}` : 'none')};
  outline-offset: -1.5px;
  &:hover {
    background: ${p => (p.$drop ? colors.primaryLight : p.$active || p.$selected ? colors.primaryExtraLight : colors.background.medium)};
  }
  & .menu {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    background: inherit;
  }
  &:hover .menu,
  & .menu:focus-visible {
    opacity: 1;
  }
  ${p => (p.$menu ? '&:hover .count { visibility: hidden; }' : '')}
`
const Name = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const Count = styled.span`
  font: 400 12px/1 ${fontFamily};
  color: ${colors.text.muted};
  width: 28px;
  text-align: right;
`
const Toggle = styled.span`
  display: inline-flex;
  width: 16px;
  justify-content: center;
  color: ${colors.text.muted};
`
const ViewsDivider = styled.div`
  height: 1px;
  margin: 8px 8px 8px 8px;
  background: ${colors.sectionDivider};
`

function readFileIds(dt) {
  try {
    return JSON.parse(dt.getData(FILE_IDS_TYPE) || '[]')
  } catch {
    return []
  }
}
function readFolderIds(dt) {
  try {
    return JSON.parse(dt.getData(FOLDER_IDS_TYPE) || '[]')
  } catch {
    return []
  }
}

export function FolderTree({
  folders,
  files,
  excludedCount = 0,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onMove,
  onDelete,
  onDropFiles,
  onDropFolders,
  selection,
  historyKey,
  initialCollapsed = [],
  onCollapsedChange
}) {
  const [collapsed, setCollapsed] = useState(() => new Set(initialCollapsed))
  const [drop, setDrop] = useState(null) // folder id (or 'all') under a file/folder drag
  const [draggingFolder, setDraggingFolder] = useState(null)
  const [menu, setMenu] = useState(null) // { el, folder }

  useEffect(() => {
    setCollapsed(new Set(initialCollapsed))
  }, [historyKey])

  const children = new Map()
  for (const g of folders) {
    const key = g.parentId || null
    if (!children.has(key)) children.set(key, [])
    children.get(key).push(g)
  }

  const dropProps = id => ({
    onDragOver: e => {
      if (e.dataTransfer.types.includes(FILE_IDS_TYPE)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (drop !== id) setDrop(id)
        return
      }
      if (e.dataTransfer.types.includes(FOLDER_IDS_TYPE)) {
        const ids = readFolderIds(e.dataTransfer)
        const invalid = id !== 'all' && ids.some(sourceId => sourceId === id || subtreeIds(folders, sourceId).has(id))
        if (invalid) {
          setDrop(null)
          return
        }
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (drop !== id) setDrop(id)
      }
    },
    onDragLeave: () => setDrop(d => (d === id ? null : d)),
    onDrop: e => {
      e.preventDefault()
      setDrop(null)
      const ids = readFileIds(e.dataTransfer)
      if (ids.length) onDropFiles(ids, id === 'all' ? null : id)
      const folderIds = readFolderIds(e.dataTransfer)
      if (folderIds.length && onDropFolders) {
        const invalid = id !== 'all' && folderIds.some(sourceId => sourceId === id || subtreeIds(folders, sourceId).has(id))
        if (!invalid) onDropFolders(folderIds, id === 'all' ? null : id)
      }
    }
  })

  const startFolderDrag = (e, folder) => {
    if (e.target.closest?.('button, input, a, img')) {
      e.preventDefault()
      return
    }
    const ids = selection?.isSelected(folder.id) ? folders.filter(g => selection.isSelected(g.id)).map(g => g.id) : [folder.id]
    e.dataTransfer.setData(FOLDER_IDS_TYPE, JSON.stringify(ids))
    e.dataTransfer.effectAllowed = 'move'
    setDraggingFolder(folder.id)
  }

  const onPath = new Set()
  {
    const byId = new Map(folders.map(g => [g.id, g]))
    for (let g = byId.get(activeId); g?.parentId; g = byId.get(g.parentId)) onPath.add(g.parentId)
  }

  const renderFolder = (g, depth) => {
    const kids = children.get(g.id) || []
    const open = !collapsed.has(g.id)
    return (
      <div key={g.id}>
        <Row
          data-testid="folder-item"
          data-folder-name={g.name}
          data-folder-id={g.id}
          data-depth={depth}
          data-active={activeId === g.id}
          data-drop={drop === g.id}
          data-selected={selection?.isSelected(g.id) || false}
          data-dragging={draggingFolder === g.id}
          $depth={depth}
          $active={activeId === g.id}
          $drop={drop === g.id}
          $selected={selection?.isSelected(g.id) || false}
          $menu
          draggable
          onDragStart={e => startFolderDrag(e, g)}
          onDragEnd={() => {
            setDraggingFolder(null)
            setDrop(null)
          }}
          onClick={() => onSelect(g.id)}
          {...dropProps(g.id)}>
          <Toggle
            onClick={e => {
              e.stopPropagation()
              setCollapsed(s => {
                const n = new Set(s)
                n.has(g.id) ? n.delete(g.id) : n.add(g.id)
                onCollapsedChange?.([...n])
                return n
              })
            }}
            data-testid="folder-toggle"
            style={{ visibility: kids.length ? 'visible' : 'hidden' }}>
            {open ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </Toggle>
          {onPath.has(g.id) && open ? (
            <FolderOpenOutlinedIcon sx={{ fontSize: 16, color: `${colors.primary}BF` }} />
          ) : (
            <FolderOutlinedIcon sx={{ fontSize: 16, color: colors.text.body }} />
          )}
          <Name data-testid="folder-name">{g.name}</Name>
          <Count className="count" data-testid="folder-count">
            {g.totalFileCount ?? 0}
          </Count>
          <IconOnlyButton
            className="menu"
            style={{ padding: 2 }}
            data-testid="folder-menu"
            title="Folder actions"
            onClick={e => {
              e.stopPropagation()
              setMenu({ el: e.currentTarget, folder: g })
            }}>
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconOnlyButton>
        </Row>
        {open && kids.map(k => renderFolder(k, depth + 1))}
      </div>
    )
  }

  const act = fn => () => {
    const g = menu.folder
    setMenu(null)
    fn(g)
  }
  return (
    <Column data-testid="folder-tree">
      <Head>
        <Subheading style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', color: colors.text.muted }}>Folders</Subheading>
        <IconOnlyButton onClick={() => onCreate(null)} data-testid="new-folder-btn" title="New folder" style={{ color: colors.primary }}>
          <CreateNewFolderOutlinedIcon sx={{ fontSize: 18 }} />
        </IconOnlyButton>
      </Head>
      <Scroll>
        <Row
          data-testid="folder-all"
          data-active={activeId === 'all'}
          data-drop={drop === 'all'}
          $depth={0}
          $active={activeId === 'all'}
          $drop={drop === 'all'}
          onClick={() => onSelect('all')}
          {...dropProps('all')}>
          <Toggle style={{ visibility: 'hidden' }} />
          <HomeOutlinedIcon sx={{ fontSize: 16, color: activeId === 'all' ? colors.primary : colors.text.body }} />
          <Name>All files</Name>
          <Count className="count" data-testid="folder-count">
            {files.length}
          </Count>
        </Row>
        {(children.get(null) || []).map(g => renderFolder(g, 0))}
        <ViewsDivider />
        <Row
          data-testid="folder-excluded"
          data-active={activeId === 'excluded'}
          $depth={0}
          $active={activeId === 'excluded'}
          onClick={() => onSelect('excluded')}>
          <Toggle style={{ visibility: 'hidden' }} />
          <VisibilityOffOutlinedIcon sx={{ fontSize: 16, color: activeId === 'excluded' ? colors.primary : colors.text.body }} />
          <Name>Excluded</Name>
          <Count className="count" data-testid="folder-count">
            {excludedCount}
          </Count>
        </Row>
      </Scroll>
      <MutedText style={{ padding: '12px 20px 18px', fontSize: 12 }}>
        Counts include subfolders. Drag files or folders onto a destination to move them.
      </MutedText>

      <ActionMenu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={act(g => onCreate(g.id))} data-testid="new-subfolder">
          <CreateNewFolderOutlinedIcon /> New subfolder
        </MenuItem>
        <MenuItem onClick={act(onRename)} data-testid="rename-folder">
          <EditOutlinedIcon /> Rename
        </MenuItem>
        <MenuItem onClick={act(onMove)} data-testid="move-folder">
          <DriveFileMoveOutlinedIcon /> Move to…
        </MenuItem>
        <Divider />
        <MenuItem onClick={act(onDelete)} data-testid="delete-folder" className="danger">
          <DeleteOutlineIcon /> Delete…
        </MenuItem>
      </ActionMenu>
    </Column>
  )
}

export function moveTargets(folders, folder) {
  const blocked = subtreeIds(folders, folder.id)
  return folders.filter(g => !blocked.has(g.id))
}

export function folderPath(folders, id) {
  const byId = new Map(folders.map(g => [g.id, g]))
  const parts = []
  for (let g = byId.get(id); g; g = g.parentId ? byId.get(g.parentId) : null) parts.unshift(g.name)
  return parts.join(' / ')
}
